import { logger } from "@/modules/logger";
import { uuidModule } from "@/modules/uuid";
import { AssistantProjectModelData } from "@/store/assistant/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { IUpdateAssistantProjectOperation } from "@/store/sync/operations/types";
import { OptimisticSyncUpdate, SyncModelData } from "@/store/sync/types";
import { getLoggableOperation } from "@/store/sync/utils";

const hasPayloadField = (
  payload: IUpdateAssistantProjectOperation["payload"],
  field: keyof IUpdateAssistantProjectOperation["payload"]
) => Object.prototype.hasOwnProperty.call(payload, field);

export class UpdateAssistantProjectOperation extends BaseSyncOperation<IUpdateAssistantProjectOperation> {
  get operationKind(): "UPDATE_ASSISTANT_PROJECT" {
    return "UPDATE_ASSISTANT_PROJECT";
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const existingProject = await this.store.assistantProjects.getAsync(this.payload.id);
    if (!existingProject) {
      logger.error({
        message: "Attempted to update assistant project that does not exist",
        info: {
          operation: getLoggableOperation(this),
        },
      });
      return [];
    }

    const modelData = existingProject.modelData;
    const projectUpdate = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: this.payload.id,
        model_kind: "ASSISTANT_PROJECT",
        model_version: existingProject.modelVersion,
        model_data: {
          ...modelData,
          title: this.payload.title ?? modelData.title,
          description: hasPayloadField(this.payload, "description")
            ? (this.payload.description ?? null)
            : modelData.description,
          status: this.payload.status ?? modelData.status,
          updated_at: this.committedAt,
        },
        model_scopes: existingProject.modelScopes,
      },
    } as unknown as OptimisticSyncUpdate<AssistantProjectModelData>;

    return [projectUpdate];
  }

  async triggerRecompute(): Promise<void> {
    await this.store.assistantProjects.recompute(this.payload.id);
  }
}
